import React, { useState } from 'react';
import { X, FileText, AlertCircle, CheckCircle, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { getBuildingWithComparison } from './firebaseB2BService';
import { calculateBuildingRiskScore } from './riskScoringEngine.js';
import { pdf } from '@react-pdf/renderer';
import BuildingReportPDF from './BuildingReportPDF';

function SnapshotPurchaseModal({ onClose, colors }) {
  const [step, setStep] = useState('address'); // address, payment, processing, success, error
  const [address, setAddress] = useState('');
  const [buildingData, setBuildingData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Handle address lookup
  const handleAddressLookup = async () => {
    if (!address.trim()) {
      setError('Please enter a valid address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch building data from Firebase with area comparison
      const building = await getBuildingWithComparison(address);

      // Calculate risk score
      const risk = calculateBuildingRiskScore(building);

      setBuildingData(building);
      setRiskData(risk);
      setStep('payment');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle payment (for now, we'll simulate payment)
  // In production, you would integrate with Stripe's payment element
  const handlePayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStep('processing');

    try {
      // Simulate payment processing
      // In production, you would call Stripe API here
      await new Promise(resolve => setTimeout(resolve, 2000));

      setStep('success');
    } catch (err) {
      setError('Payment failed. Please try again.');
      setStep('payment');
    } finally {
      setLoading(false);
    }
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    try {
      const blob = await pdf(<BuildingReportPDF building={buildingData} riskData={riskData} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Toice_Risk_Report_${buildingData.address.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  return (
    <div className="modal-backdrop" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div className="modal-content" style={{
        backgroundColor: 'white',
        borderRadius: '1rem',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#64748b',
            padding: '0.5rem'
          }}
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div style={{
          padding: '2rem',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
            <FileText style={{ color: colors.primary }} size={32} />
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>
              Purchase Risk Intelligence Report
            </h2>
          </div>
          <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
            Get a comprehensive risk analysis for $499
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '2rem' }}>
          {/* Step 1: Address Entry */}
          {step === 'address' && (
            <div>
              <label style={{ display: 'block', fontWeight: '600', color: '#0f172a', marginBottom: '0.5rem' }}>
                Property Address
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddressLookup()}
                placeholder="Enter full building address"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  marginBottom: '1rem',
                  boxSizing: 'border-box'
                }}
              />

              {error && (
                <div style={{
                  backgroundColor: '#fee2e2',
                  border: '1px solid #ef4444',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <AlertCircle size={20} style={{ color: '#dc2626' }} />
                  <p style={{ color: '#991b1b', margin: 0, fontSize: '0.875rem' }}>{error}</p>
                </div>
              )}

              <div style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ color: '#0369a1', margin: 0, fontSize: '0.875rem', lineHeight: '1.5' }}>
                  <strong>Note:</strong> Reports are only available for buildings with at least 3 tenant reviews.
                  If your building isn't in our system, we'll let you know.
                </p>
              </div>

              <button
                onClick={handleAddressLookup}
                disabled={loading || !address.trim()}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: loading || !address.trim() ? '#cbd5e1' : colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: loading || !address.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Searching...' : 'Check Availability'}
              </button>
            </div>
          )}

          {/* Step 2: Payment */}
          {step === 'payment' && buildingData && riskData && (
            <div>
              {/* Building Preview */}
              <div style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0f172a', marginBottom: '1rem' }}>
                  Building Found
                </h3>
                <p style={{ fontSize: '1rem', fontWeight: '600', color: '#0f172a', marginBottom: '0.5rem' }}>
                  {buildingData.address}
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginTop: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Risk Score</div>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: riskData.riskScore >= 70 ? '#dc2626' : riskData.riskScore >= 50 ? '#ea580c' : '#10b981'
                    }}>
                      {riskData.riskScore}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.25rem' }}>Total Issues</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0f172a' }}>
                      {riskData.totalIssues}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Form */}
              <form onSubmit={handlePayment}>
                <div style={{
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.25rem' }}>
                    $499
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                    One-time payment • Instant delivery
                  </div>
                </div>

                {/* Stripe Payment would go here */}
                <div style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fbbf24',
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  <p style={{ color: '#92400e', margin: 0, fontSize: '0.875rem' }}>
                    <strong>Demo Mode:</strong> Stripe integration will be added. Click "Complete Purchase" to preview the report.
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    type="button"
                    onClick={() => setStep('address')}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      backgroundColor: '#f1f5f9',
                      color: '#475569',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 2,
                      padding: '0.75rem',
                      backgroundColor: colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Complete Purchase
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Step 3: Processing */}
          {step === 'processing' && (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                border: '4px solid #e2e8f0',
                borderTop: '4px solid ' + colors.primary,
                borderRadius: '50%',
                margin: '0 auto 1.5rem',
                animation: 'spin 1s linear infinite'
              }} />
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#0f172a', marginBottom: '0.5rem' }}>
                Processing Payment
              </h3>
              <p style={{ color: '#64748b', fontSize: '0.875rem' }}>
                Please wait while we generate your report...
              </p>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 'success' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '64px',
                height: '64px',
                backgroundColor: '#d1fae5',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <CheckCircle size={40} style={{ color: '#10b981' }} />
              </div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0f172a', marginBottom: '0.5rem' }}>
                Report Ready!
              </h3>
              <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '2rem' }}>
                Your Toice Risk Intelligence Report has been generated and is ready to download.
              </p>

              <button
                onClick={handleDownloadPDF}
                style={{
                  width: '100%',
                  padding: '1rem',
                  backgroundColor: colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1.125rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  marginBottom: '1rem'
                }}
              >
                <Download size={20} />
                Download PDF Report
              </button>

              <button
                onClick={onClose}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add spinning animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default SnapshotPurchaseModal;
